import { useSetAtom } from "jotai";
import { useCallback, useMemo, useRef } from "react";
import type { Components } from "react-markdown";
import { amaGuideOpenRequestAtom } from "~/atoms/amaGuide";
import { appointmentDocumentOpenRequestAtom } from "~/atoms/appointmentDocument";
import {
  type AmaGuideOpenRequest,
  type AmaGuideOpenResult,
  type AppointmentDocumentOpenRequest,
  buildChatbotLinkComponent,
  type ChatbotLinkContext,
} from "~/pages/appointments/components/chatbotLinks";

interface MarkdownLinkOverrides {
  appointmentDocumentDisabledReason?: string;
  onRequestAmaGuide?: (
    req: AmaGuideOpenRequest,
  ) => Promise<AmaGuideOpenResult> | void;
  onRequestAppointmentDocument?: (req: AppointmentDocumentOpenRequest) => void;
}

/**
 * The click context chatbot content is rendered against: the viewer openers.
 * Callers (the floating panel, the full-screen page) can pass explicit handlers (e.g. the IR sidebar's AMA viewer); when omitted we
 * fall back to the cross-surface atoms so any mounted viewer picks up the
 * click no matter where the chat is rendered.
 *
 * Shared by the markdown link components and the typed-data-block renderers,
 * so a card's click and a link's click reach the same viewers.
 */
export function useChatbotLinkContext(
  overrides: MarkdownLinkOverrides = {},
): ChatbotLinkContext {
  const {
    onRequestAmaGuide,
    onRequestAppointmentDocument,
    appointmentDocumentDisabledReason,
  } = overrides;
  const setAmaGuideRequest = useSetAtom(amaGuideOpenRequestAtom);
  const setAppointmentDocumentRequest = useSetAtom(
    appointmentDocumentOpenRequestAtom,
  );
  // Per-instance counters so repeated clicks on the same link still register as
  // new events to downstream consumers (which key off the token).
  const amaRequestTokenRef = useRef(0);
  const appointmentDocRequestTokenRef = useRef(0);

  const defaultOnRequestAmaGuide = useCallback(
    (req: AmaGuideOpenRequest): Promise<AmaGuideOpenResult> | void => {
      amaRequestTokenRef.current += 1;
      setAmaGuideRequest({ ...req, token: amaRequestTokenRef.current });
    },
    [setAmaGuideRequest],
  );

  const defaultOnRequestAppointmentDocument = useCallback(
    (req: AppointmentDocumentOpenRequest): void => {
      appointmentDocRequestTokenRef.current += 1;
      setAppointmentDocumentRequest({
        ...req,
        token: appointmentDocRequestTokenRef.current,
      });
    },
    [setAppointmentDocumentRequest],
  );

  return useMemo(
    () => ({
      appointmentDocumentDisabledReason,
      onRequestAmaGuide: onRequestAmaGuide ?? defaultOnRequestAmaGuide,
      onRequestAppointmentDocument:
        onRequestAppointmentDocument ?? defaultOnRequestAppointmentDocument,
    }),
    [
      appointmentDocumentDisabledReason,
      onRequestAmaGuide,
      defaultOnRequestAmaGuide,
      onRequestAppointmentDocument,
      defaultOnRequestAppointmentDocument,
    ],
  );
}

export interface ChatbotRendering {
  /** react-markdown components for prose. */
  markdownComponents: Components;
  /**
   * The same context those components dispatch through, handed to the typed
   * data-block renderers too. Returned rather than rebuilt inside
   * `ChatMessageBody` so a caller's overrides (the IR sidebar's AMA viewer,
   * the Case Documents drawer) reach a card's click as well as a link's —
   * otherwise the next block kind that opens a viewer routes to the default
   * atom handlers instead of the caller's.
   */
  linkContext: ChatbotLinkContext;
}

/**
 * Build everything chatbot message content is rendered against: the
 * react-markdown components for prose, and the click context they share with
 * the entity-component renderers.
 */
export function useChatbotRendering(
  overrides: MarkdownLinkOverrides = {},
): ChatbotRendering {
  const linkContext = useChatbotLinkContext(overrides);
  return useMemo(
    () => ({
      markdownComponents: { a: buildChatbotLinkComponent(linkContext) },
      linkContext,
    }),
    [linkContext],
  );
}
